import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { QueryFunctionContext, useQueries, useQueryClient } from "react-query";

import { NOT_AVAILABLE } from "constants/app.constants";

import { WellDetailCardData, getWellDetailCard } from "api/data";
import { fetchWellDetail } from "api/wellDetail";

import { Uwi } from "models";
import { EntityKind } from "models/entityKind";

const TYPE_LOG_GROUP = "Type Log";

/**
 * Fields per request. The whole list travels in the query string, and the geo
 * groups carry hundreds of columns, so it is split into requests no URL can
 * outgrow.
 */
export const FIELD_CHUNK_SIZE = 25;

/**
 * Characters a chunk's encoded field list may take in the query string. A
 * count alone does not bound a URL: geo field names are org-authored and long,
 * and 25 of them can outrun the length a proxy will accept while 25 header
 * fields sit nowhere near it.
 */
export const FIELD_CHUNK_MAX_URL_LENGTH = 1_500;

/**
 * Milliseconds one chunk may take. A client-side backstop, not a margin under
 * anything: it is here so one stuck chunk cannot hold the group's serial queue
 * open for ever, leaving the tab on a spinner with the chunks behind it never
 * sent. The real ceilings are the ingress timeouts, which differ per
 * environment, and this must stay well clear of them.
 */
export const CHUNK_TIMEOUT_MS = 120_000;

function chunkFields(
  fields: string[],
  size = FIELD_CHUNK_SIZE,
  maxLength = FIELD_CHUNK_MAX_URL_LENGTH
): string[][] {
  const chunks: string[][] = [];
  let current: string[] = [];
  let length = 0;
  for (const field of fields) {
    // The separator each field adds once it is not the first one.
    const encodedLength = encodeURIComponent(field).length + 1;
    const isFull = current.length >= size || length + encodedLength > maxLength;
    if (current.length > 0 && isFull) {
      chunks.push(current);
      current = [];
      length = 0;
    }
    current.push(field);
    length += encodedLength;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

interface FetchGroupFieldsParams {
  uwid: string;
  group: string;
  /** The one subgroup this request covers, or null for the whole group. */
  subgroup: Maybe<string>;
  entityKind: EntityKind;
  fields: string[];
  defaultSignificantFigures?: number;
  useStrictRounding?: boolean;
  /** Called with the values so far each time a chunk lands. */
  onPartial?: (partial: WellDetailCardData) => void;
  /** Aborted when this query is dropped — see the loop's own bail-out below. */
  signal?: AbortSignal;
}

/**
 * The endpoint puts the display uwi in the same `fields` map as the columns.
 * It is not one, and the map tooltip filters it out for the same reason.
 */
const FORMATTED_UWI_FIELD = "formattedUwi";

/**
 * The endpoint answers in JSON, so a value can arrive as something other than
 * a string: integer columns come back as numbers, and floats already arrive
 * pre-formatted as strings. A number renders identically whether React prints
 * it or this casts it, so the cast keeps `WellDetailCardData`'s string
 * contract without changing what the map tooltip shows for the same field.
 *
 * Anything else — a boolean, an object, a null — the tooltip renders as
 * nothing at all. "n/a" is how this card says nothing, so that is what those
 * become, rather than "true" or "[object Object]".
 */
function toFieldText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return NOT_AVAILABLE;
}

/**
 * One group's values, read from the well-detail endpoint the map tooltip uses.
 * That endpoint answers per field: a field it cannot resolve comes back as
 * "n/a" and every other field still arrives. The POST it replaces answered 204
 * for the whole group as soon as one field returned nothing — routine for
 * custom fields (geom-bin pseudo-columns, unfinished geo calcs, project
 * columns), and the reason whole tabs rendered their headings with no rows.
 *
 * The response is JSON, so an integer column arrives as a number. Values are
 * cast back to strings to keep `WellDetailCardData`'s shape and the rendering
 * the POST gave.
 *
 * Chunks run one after another, not together. The service resolves a group's
 * fields sequentially either way, so running the chunks in parallel bought no
 * work back — it only stretched every chunk's wall-clock time towards the
 * whole group's, which pushed all of them past the ingress read timeout at
 * once. One at a time, each request is short and finished work is kept.
 *
 * A failed chunk is collected and the next one still runs: it must not throw
 * away the values the others returned, which would put the request straight
 * back to the all-or-nothing behaviour this endpoint was chosen to escape. The
 * fields behind a failed chunk are simply absent and render as "n/a" rows.
 * Only every chunk failing is the request failing — and a request covers one
 * subgroup wherever the card asks per subgroup, so an org field nothing can
 * resolve fails its own dropdown rather than the whole tab.
 *
 * `onPartial` is handed the values so far after every chunk that lands, so the
 * tab fills in as the group arrives rather than holding a spinner until the
 * last chunk. A geo group is hundreds of fields and minutes of work; waiting
 * for all of it before showing any of it is what made the tab look hung.
 */
async function fetchGroupFields({
  uwid,
  group,
  subgroup,
  entityKind,
  fields,
  defaultSignificantFigures,
  useStrictRounding,
  onPartial,
  signal
}: FetchGroupFieldsParams): Promise<WellDetailCardData> {
  const chunks = chunkFields(fields);
  const merged: WellDetailCardData = {};
  const failures: unknown[] = [];

  for (const chunk of chunks) {
    // The loop outlives the request that started it: a geo group is minutes of
    // chunks, and picking another well drops this query while they are still
    // going. Without this, every retarget stacks another run against the
    // service and the rest of the chunks are work nothing will ever read.
    if (signal?.aborted) break;
    try {
      const response = await fetchWellDetail({
        uwi: uwid,
        isFacility: entityKind === EntityKind.Facility,
        fields: chunk,
        defaultSignificantFigures,
        useStrictRounding,
        timeout: CHUNK_TIMEOUT_MS,
        signal
      });
      // A chunk that asked for fields and got none back has failed, whatever
      // the status says. The service answers 200 with an empty map when it
      // gives up on a field — one field it cannot resolve ends the whole
      // chunk's work — so the status alone cannot see its own failures, and
      // merging that answer would turn 25 fields into authoritative "n/a" rows
      // with nothing reported and no Retry offered.
      const values = response?.status === 200 ? response.data?.fields ?? {} : null;
      if (!values || Object.keys(values).length === 0) {
        failures.push(
          new Error(
            `well-detail answered ${response?.status} with no fields for ${chunk.length} asked for`
          )
        );
        continue;
      }
      Object.entries(values).forEach(([field, value]) => {
        if (field === FORMATTED_UWI_FIELD) return;
        merged[field] = toFieldText(value);
      });
      // A fresh object every time: the readers downstream compare by identity.
      onPartial?.({ ...merged });
    } catch (err) {
      failures.push(err);
    }
  }

  // A dropped query answers to nobody: no error state to reach, and no one
  // left to tell about the chunks that did not run.
  if (signal?.aborted) return merged;

  // Nothing arrived, so this is the request's own failure: rethrow the first
  // reason and let the query's error state and onError logging handle it,
  // exactly as an unchunked request did.
  if (chunks.length > 0 && failures.length === chunks.length) {
    throw failures[0];
  }

  // A partial answer is still shown, so nothing surfaces to the user — one
  // capture per group is what tells us the tab is quietly short of fields.
  // Fingerprinted on the group rather than the well: the same short tab for
  // every well in a region is one problem, and the uwi would make it a separate
  // Sentry issue each time. The subgroup stays out of the fingerprint for the
  // same reason — job names are org-authored, so folding one in would split
  // this into an issue per job per org. It is in the context below, which is
  // where that detail belongs.
  if (failures.length > 0) {
    Sentry.withScope((scope) => {
      scope.setContext("fetchGroupFields", {
        uwid,
        group,
        subgroup,
        entityKind,
        failedChunks: failures.length,
        totalChunks: chunks.length
      });
      scope.setFingerprint(["fetchGroupFields", group, String(entityKind)]);
      scope.setLevel("warning");
      Sentry.captureException(failures[0]);
    });
  }

  return merged;
}

/**
 * What the queryFn stores for a chunked group: the fields so far, and whether
 * every chunk has landed. Internal to this hook — `next` below unwraps it.
 */
interface ChunkEnvelope {
  fields: WellDetailCardData;
  complete: boolean;
}

function asChunkEnvelope(data: unknown): Maybe<ChunkEnvelope> {
  return data && typeof data === "object" && "complete" in data
    ? (data as ChunkEnvelope)
    : null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * One request's fetch parameters. `fields` is computed once, at the moment the
 * request is first made (see `useWellDetailsData.tsx`), and is treated as part
 * of the cache identity — a different `fields` array for the same `group` is a
 * different query, not a refetch of the same one.
 *
 * `subgroup` names the one dropdown this request covers; null is the whole
 * group, which is what every tab that is not split into dropdowns asks for.
 */
export interface WellDetailGroupRequest {
  group: string;
  subgroup?: Maybe<string>;
  fields: string[];
}

export interface WellDetailGroupQueryResult {
  group: string;
  /** Echoed back so the caller can tell a dropdown's result from its tab's. */
  subgroup: Maybe<string>;
  data: Maybe<unknown>;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  /**
   * Chunks are still arriving. A group is `isSuccess` from its first chunk on,
   * so this — not `isLoading` — is what says the values on screen are not the
   * whole group yet.
   */
  isFetching: boolean;
  error: unknown;
  // Explicit retry escape hatch (code review): with retry:false, a group
  // whose query settled in an error state is never automatically re-fetched
  // just because its tab is revisited (unlike the old success-tracked
  // loadedGroups guard, which let re-selecting an errored tab retry it).
  // useWellDetailsData calls this when the current group's result isError.
  refetch: () => void;
}

interface UseWellDetailGroupQueryParams {
  uwid: Maybe<string>;
  entityKind: EntityKind;
  requests: WellDetailGroupRequest[];
  defaultSignificantFigures?: number;
  useStrictRounding?: boolean;
}

// ─── Query hook ──────────────────────────────────────────────────────────────

/**
 * `useQueries` over a caller-supplied `requests` list (one entry per group
 * currently requested for this widget instance). Replaces the old
 * `WellDetailsContext` reducer's imperative `getWellDetailCard` mutation +
 * `appendWellDetails`/`setGroupWellDetails`/`setTypeLogPdf` dispatch calls —
 * React Query now owns the fetch lifecycle and per-key cache directly.
 *
 * `retry: false` matches the old `useMutation`-based fetch, which never
 * auto-retried on 401/404/generic errors — `useQuery`'s v3 default (3
 * retries) would otherwise be a behavior change, not a straight port.
 */
export function useWellDetailGroupQuery({
  uwid,
  entityKind,
  requests,
  defaultSignificantFigures,
  useStrictRounding
}: UseWellDetailGroupQueryParams): WellDetailGroupQueryResult[] {
  const settingsKey = `${defaultSignificantFigures}:${useStrictRounding}`;
  const resultsRef = useRef<WellDetailGroupQueryResult[]>([]);
  const queryClient = useQueryClient();

  const queries = useQueries(
    requests.map(({ group, subgroup, fields }) => {
      const queryKey = [
        "wellDetailGroup",
        uwid,
        entityKind,
        group,
        subgroup ?? null,
        fields,
        settingsKey
      ];
      return {
        queryKey,
        // `signal` is read here on purpose, not only used: reading it is what
        // tells React Query v3 this query is cancellable, so dropping the last
        // observer — a retarget, or the card closing — aborts the request in
        // flight instead of letting it and the chunks behind it run on.
        queryFn: async ({ signal }: QueryFunctionContext) => {
          // Only the Type Log still needs /data/single: it answers with an
          // arraybuffer holding the PDF, which the well-detail endpoint has no
          // shape for.
          if (group === TYPE_LOG_GROUP) {
            const response = await getWellDetailCard({
              uwid,
              group,
              fields,
              entityKind,
              defaultSignificantFigures,
              useStrictRounding
            });
            return response.data;
          }
          const groupFields = await fetchGroupFields({
            uwid,
            group,
            subgroup,
            entityKind,
            fields,
            defaultSignificantFigures,
            useStrictRounding,
            signal,
            // Publishing each chunk into this query's own cache entry is what
            // makes the tab progressive: the first chunk moves the query out of
            // `loading` into `success`, so the group counts as loaded and its
            // rows render, and every later chunk replaces the data with a
            // larger record. The resolved promise writes the complete one last.
            //
            // `complete` rather than react-query's `isFetching`: writing to the
            // cache dispatches a success, which clears `isFetching` even though
            // the promise is still running, so it cannot say whether the rest of
            // the group is still coming. Unwrapped below, so nothing downstream
            // sees this envelope.
            //
            // Not written once the query is dropped: this key belongs to the
            // well that has just been left, and reviving it would put the
            // previous well's values back in the cache.
            onPartial: (partial) => {
              if (signal?.aborted) return;
              queryClient.setQueryData(queryKey, { fields: partial, complete: false });
            }
          });
          return { fields: groupFields, complete: true };
        },
        // A group with no columns has nothing to ask for, and asking anyway is
        // a 400 from /data/single and an empty answer from well-detail.
        enabled: !!uwid && fields.length > 0,
        retry: false,
        // Log once per failure here (matches the old mutation's onError)
        // rather than in deriveWellDetailsMessages, which runs in the render
        // path and would otherwise re-log on every re-render while the error
        // persists.
        onError: (err: unknown) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response = (err as any)?.response;
          if (response?.status !== 401 && response?.status !== 404 && response?.data) {
            // eslint-disable-next-line no-console
            console.error(response.data);
          }
        }
      };
    })
  );

  // Referential stability guard: react-query v3's `useQueries` returns a NEW
  // array of NEW result objects on every render, so a plain `requests.map`
  // here changes identity every render even when nothing changed. Downstream,
  // `useWellDetailsData` derives `loadedGroups`/`wellDetails` via `useMemo`
  // keyed on this hook's return, and `WellCard`'s tabs-effect depends on
  // `loadedGroups` — an always-new identity makes that effect fire on every
  // render and `setTabs(newArray)` re-render again: a self-sustaining update
  // loop that crashes with React #185 ("Maximum update depth exceeded") as
  // soon as anything flushes it synchronously (e.g. antd Dropdown's
  // onPopupVisibleChange on the widget's ⋯ menu). Reuse the previous array
  // when every observable field is unchanged. `refetch` is deliberately
  // excluded from the comparison — it is bound to the persistent
  // QueryObserver, so the previous render's reference stays valid.
  const next = requests.map(({ group, subgroup, fields }, index) => {
    const query = queries[index];
    // A request with no fields is reported as settled and empty. Its query is
    // disabled, so it would otherwise never leave the loading state and its
    // tab would spin behind a request that never runs. This is also how a tab
    // split into dropdowns says its shell is ready: it asks for no fields of
    // its own, and each dropdown asks for its own when it is opened.
    if (fields.length === 0) {
      return {
        group,
        subgroup: subgroup ?? null,
        data: undefined,
        isLoading: false,
        isSuccess: true,
        isError: false,
        isFetching: false,
        error: null,
        refetch: query.refetch
      };
    }
    // Unwrap the chunk envelope the queryFn publishes, so every reader below
    // still sees the flat field dict this hook has always returned. The Type
    // Log's arraybuffer carries no envelope and passes through untouched.
    const envelope = asChunkEnvelope(query.data);
    return {
      group,
      subgroup: subgroup ?? null,
      data: envelope ? envelope.fields : query.data,
      isLoading: query.isLoading,
      isSuccess: query.isSuccess,
      isError: query.isError,
      isFetching: envelope ? !envelope.complete : query.isFetching,
      error: query.error,
      refetch: query.refetch
    };
  });

  // `data` and `isFetching` are both in the comparison. Each landed chunk
  // writes a new record into the cache, so `data` changes identity a bounded
  // number of times per group — once per chunk — and the array is rebuilt
  // then, which is exactly what puts the new rows on screen. Renders where no
  // chunk landed still reuse the previous array, so the guard holds.
  const prev = resultsRef.current;
  const unchanged =
    prev.length === next.length &&
    next.every((n, i) => {
      const p = prev[i];
      return (
        p.group === n.group &&
        p.subgroup === n.subgroup &&
        p.data === n.data &&
        p.isLoading === n.isLoading &&
        p.isSuccess === n.isSuccess &&
        p.isError === n.isError &&
        p.isFetching === n.isFetching &&
        p.error === n.error
      );
    });

  const results = unchanged ? prev : next;
  resultsRef.current = results;
  return results;
}

// ─── Merge helper ────────────────────────────────────────────────────────────

/**
 * Merges the per-group query results into the single `WellDetailCardData`
 * dict `WellCard.tsx` (and its child `WellCardDataValue`) reads property
 * values from. Reproduces the old reducer's merge semantics exactly:
 *  - "Type Log" group data lands under the numeric `[0]` slot as a raw
 *    PDF-blob payload only for `EntityKind.Well` (`setTypeLogPdf`'s old
 *    guard: `variables.group === "Type Log" && entityKind === EntityKind.Well`)
 *    — an existing quirk, preserved as-is, not "fixed" (FR-005). A facility
 *    fetch of that group falls through to the generic shallow merge, same as
 *    the old reducer's `else if (variables.group)` branch.
 *  - every other group's data is shallow-merged in by field name
 *    (`appendWellDetails`/`setGroupWellDetails`).
 * Only successful results contribute — a still-loading or errored group
 * simply doesn't overwrite/contribute a value (matching the old code, which
 * never merged anything from a request that hadn't yet resolved with data).
 */
export function mergeWellDetailResults(
  results: WellDetailGroupQueryResult[],
  entityKind: EntityKind
): WellDetailCardData {
  return results.reduce<WellDetailCardData>((merged, result) => {
    if (!result.isSuccess) return merged;
    if (result.group === TYPE_LOG_GROUP && entityKind === EntityKind.Well) {
      return { ...merged, 0: result.data as unknown as string };
    }
    return { ...merged, ...(result.data as WellDetailCardData) };
  }, {});
}

// ─── Error-message derivation ────────────────────────────────────────────────

export interface WellDetailsMessages {
  error: Nullable<string>;
  typeLogMessage: Nullable<string>;
}

/**
 * Re-derives the `error`/`typeLogMessage` fields the old `useWellDetailsData`
 * kept in local `useState` (set imperatively from the mutation's `onError`
 * callback) from the current group's query error state instead (Standing
 * Rule 6 — derive, don't duplicate).
 *
 * Reproduces the old branching exactly, including its one pre-existing
 * quirk: a 401/404 on *any* group's fetch (not just "Type Log") sets
 * `typeLogMessage`, since the old reducer's `onError` never guarded on
 * `request.group` — preserved as-is, not "fixed", per FR-013.
 */
export function deriveWellDetailsMessages(
  result: Maybe<Pick<WellDetailGroupQueryResult, "isError" | "error">>,
  uwid: Maybe<string>
): WellDetailsMessages {
  if (!result?.isError) {
    return { error: null, typeLogMessage: null };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = result.error as any;
  if (!err) {
    return { error: null, typeLogMessage: null };
  }

  if (err.response?.status === 401) {
    return { error: null, typeLogMessage: "Requires subscription." };
  }

  if (err.response?.status === 404) {
    return { error: null, typeLogMessage: "No type log available." };
  }

  // Generic-error logging happens once per failure in the query's onError
  // (see useWellDetailGroupQuery above) — this derive helper stays pure.
  return {
    error: `Error loading details for well ${new Uwi().toFormatted(uwid)}.`,
    typeLogMessage: null
  };
}
